import React, { useState, useEffect, useCallback } from 'react'
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Upload
} from 'antd'
import {
  PlusOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'
import { useNavigate, useParams, Link } from 'react-router-dom'
import articleService from '../services/articleService'
import { Article } from '../types/articles'
import { uploadSingleFile, deleteFileByUrl } from '../services/uploadService'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { useSnackbar } from 'notistack'

const { TextArea } = Input
const { Option } = Select

const ArticleEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [article, setArticle] = useState<Article | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [featuredImage, setFeaturedImage] = useState<string | undefined>(
    undefined
  )
  const [socialImage, setSocialImage] = useState<string | undefined>(undefined)
  const [quillValue, setQuillValue] = useState('')
  const [quillValueEn, setQuillValueEn] = useState('')
  const [metaTitleEn, setMetaTitleEn] = useState('')
  const [metaDescriptionEn, setMetaDescriptionEn] = useState('')
  const { enqueueSnackbar } = useSnackbar()

  const fetchArticle = useCallback(async () => {
    try {
      setInitialLoading(true)
      const data = await articleService.getArticleById(id!)
      setArticle(data)
      
      
      const formValues = {
        title: data.title,
        slug: data.slug,
        description: data.description,
        content: data.content,
        type: data.type,
        status: data.status,
        readingTime: data.readingTime,
        tags: data.tags || data.keywords || [],
        keywords: data.tags || data.keywords || [],
        metaTitle: data.metaTitle || '',
        metaDescription: data.metaDescription || '',
        canonicalUrl: data.canonicalUrl || ''
      }
      
      form.setFieldsValue(formValues)
    } catch (error) {
      console.error('Error fetching article:', error)
      enqueueSnackbar('Không thể tải thông tin bài viết', { variant: 'error' })
    } finally {
      setInitialLoading(false)
    }
  }, [id, form, enqueueSnackbar])

  useEffect(() => {
    if (id) {
      fetchArticle()
    }
  }, [id, fetchArticle])

  useEffect(() => {
    if (article?.featuredImage?.url) {
      setFeaturedImage(article.featuredImage.url)
    }
    if (article?.socialImage?.url) {
      setSocialImage(article.socialImage.url)
    }
    if (article?.content) {
      setQuillValue(article.content)
      form.setFieldsValue({ content: article.content })
    }
    
    // Set tags/keywords if available
    if (article && (article.tags || (article as any)?.keywords)) {
      const tagsValue = article.tags || (article as any).keywords || [];
      form.setFieldsValue({ tags: tagsValue });
    }
    
    // Set English translations if available
    const englishTranslation = (article as any)?.translations?.find((t: any) => t.type === 'en');
    if (englishTranslation) {
      setQuillValueEn(englishTranslation.content || '');
      setMetaTitleEn(englishTranslation.meta?.title || '');
      setMetaDescriptionEn(englishTranslation.meta?.description || '');
      
      form.setFieldsValue({
        title_en: englishTranslation.title || '',
        description_en: englishTranslation.description || '',
        content_en: englishTranslation.content || '',
        metaTitleEn: englishTranslation.meta?.title || '',
        metaDescriptionEn: englishTranslation.meta?.description || ''
      });
    }
  }, [article, form])

  const onFinish = async (values: any) => {
    try {
      setLoading(true)
      const submitValues = {
        ...values,
        content: quillValue,
        featuredImage: featuredImage ? { url: featuredImage, alt: values.title } : undefined,
        socialImage: socialImage ? { url: socialImage, alt: values.title } : undefined,
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        canonicalUrl: values.canonicalUrl,
        tags: values.tags || values.keywords || [],
        keywords: values.tags || values.keywords || [],
        translations: [
          {
            type: "en",
            title: values.title_en || '',
            description: values.description_en || '',
            content: quillValueEn,
            meta: {
              title: metaTitleEn,
              description: metaDescriptionEn
            }
          }
        ]
      }
      await articleService.updateArticle(id!, submitValues)
      enqueueSnackbar('Cập nhật bài viết thành công', { variant: 'success' })
      navigate('/admin/articles')
    } catch (error) {
      console.error('Error updating article:', error)
      enqueueSnackbar('Cập nhật bài viết thất bại', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/admin/articles')
  }

  if (initialLoading) {
    return (
      <div className='p-6'>
        <Card>
          <div className='text-center'>Đang tải...</div>
        </Card>
      </div>
    )
  }

  if (!article) {
    return (
      <div className='p-6'>
        <Card>
          <div className='text-center'>Không tìm thấy bài viết</div>
        </Card>
      </div>
    )
  }

  return (
    <div className='p-6'>
      <div className='mb-6'>
        <Link
          to='/admin/articles'
          className='inline-flex items-center text-blue-600 hover:text-blue-800'
        >
          <ArrowLeftOutlined className='mr-2' />
          Quay lại danh sách
        </Link>
        <h1 className='mt-4 text-2xl font-semibold text-gray-900'>
          Chỉnh sửa bài viết
        </h1>
        <p className='mt-1 text-sm text-gray-500'>
          Chỉnh sửa thông tin bài viết "{article.title}"
        </p>
      </div>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item
              name='title'
              label='Tiêu đề'
              rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
            >
              <Input
                placeholder='Nhập tiêu đề bài viết'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='slug'
              label='Slug'
              rules={[{ required: true, message: 'Vui lòng nhập slug' }]}
            >
              <Input
                placeholder='slug-bai-viet'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginLeft: '20px', marginTop: '20px' }}>
          <Form.Item
            name="title_en"
            label="Tiêu đề (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập tiêu đề tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Enter article title in English"
              style={{
                width: '100%',
                height: 32,
                borderRadius: 4,
                fontSize: 16,
                padding: '6px 12px',
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name='description'
          label='Mô tả ngắn'
          rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
        >
          <TextArea
            rows={3}
            placeholder='Nhập mô tả ngắn cho bài viết'
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-10px' }}>
          <Form.Item
            name="description_en"
            label="Mô tả ngắn (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
            style={{ marginBottom: '16px' }}
          >
            <TextArea
              rows={3}
              placeholder="Enter article description in English"
              maxLength={1000}
              showCount
              style={{
                backgroundColor: '#f5f5f5',
              }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name='content'
          label='Nội dung'
          rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
        >
          <ReactQuill
            theme='snow'
            value={quillValue}
            onChange={val => {
              setQuillValue(val)
              form.setFieldsValue({ content: val })
            }}
            placeholder='Nhập nội dung bài viết'
            style={{ borderRadius: '10px', height: '450px', marginBottom: '40px' }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [
                  { list: 'ordered' },
                  { list: 'bullet' },
                  { indent: '-1' },
                  { indent: '+1' }
                ],
                ['link', 'image'],
                ['clean']
              ]
            }}
            formats={[
              'header',
              'bold',
              'italic',
              'underline',
              'strike',
              'blockquote',
              'list',
              'bullet',
              'indent',
              'link',
              'image'
            ]}
          />
        </Form.Item>

        <div style={{ marginLeft: '20px', marginTop: '-20px' }}>
          <Form.Item
            name="content_en"
            label="Nội dung (tiếng Anh)"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung tiếng Anh' }]}
            style={{ marginBottom: '40px' }}
            getValueFromEvent={() => quillValueEn}
          >
            <ReactQuill
              theme='snow'
              value={quillValueEn}
              onChange={val => {
                setQuillValueEn(val)
                form.setFieldsValue({ content_en: val })
              }}
              placeholder='Enter article content in English'
              style={{
                borderRadius: '10px',
                height: '450px',
                marginBottom: '40px',
                backgroundColor: '#f5f5f5',
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [
                    { list: 'ordered' },
                    { list: 'bullet' },
                    { indent: '-1' },
                    { indent: '+1' }
                  ],
                  ['link', 'image'],
                  ['clean']
                ]
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image'
              ]}
            />
          </Form.Item>
        </div>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='type'
              label='Loại bài viết'
              rules={[{ required: true, message: 'Vui lòng chọn loại' }]}
            >
              <Select placeholder='Chọn loại bài viết'>
                <Option value='blog'>Tin tức sự kiện nổi bật</Option>
                <Option value='news'>Hiểu về Lụa</Option>
                <Option value='article'>Thời trang lụa</Option>
                <Option value='heritage'>Lụa & Di sản</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='status'
              label='Trạng thái'
              rules={[
                { required: true, message: 'Vui lòng chọn trạng thái' }
              ]}
            >
              <Select placeholder='Chọn trạng thái'>
                <Option value='draft'>Bản nháp</Option>
                <Option value='published'>Xuất bản</Option>
                <Option value='archived'>Lưu trữ</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='readingTime'
              label='Thời gian đọc (phút)'
              rules={[
                { required: true, message: 'Vui lòng nhập thời gian đọc' }
              ]}
            >
              <Input
                type='number'
                min={1}
                placeholder='5'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginLeft: '20px', marginTop: '20px' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='metaTitleEn' label='Meta Title (tiếng Anh)'>
                <Input
                  value={metaTitleEn}
                  onChange={e => setMetaTitleEn(e.target.value)}
                  maxLength={200}
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='metaDescriptionEn' label='Meta Description (tiếng Anh)'>
                <Input
                  value={metaDescriptionEn}
                  onChange={e => setMetaDescriptionEn(e.target.value)}
                  maxLength={500}
                  style={{
                    width: '100%',
                    height: 32,
                    borderRadius: 4,
                    fontSize: 16,
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Form.Item name='tags' label='Từ khóa (edit)'>
          <Select
            mode='tags'
            placeholder='Nhập từ khóa và nhấn Enter'
            style={{ width: '100%' }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              return String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name='metaTitle' label='Meta Title'>
              <Input
                maxLength={200}
                placeholder='Meta title cannot be more than 200 characters'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='metaDescription' label='Meta Description'>
              <Input
                maxLength={500}
                placeholder='Meta description cannot be more than 500 characters'
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='canonicalUrl' label='Canonical URL'>
              <Input
                style={{
                  width: '100%',
                  height: 32,
                  borderRadius: 4,
                  fontSize: 16,
                  padding: '6px 12px'
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name='socialImage' label='Ảnh chia sẻ mạng xã hội'>
          <Upload
            listType='picture-card'
            fileList={
              socialImage
                ? [
                    {
                      uid: '-1',
                      name: 'social.jpg',
                      status: 'done',
                      url: socialImage,
                      thumbUrl: socialImage
                    }
                  ]
                : []
            }
            customRequest={async ({ file, onSuccess }) => {
              const res = await uploadSingleFile(file as File)
              if (res.status === 'success' && onSuccess) {
                setSocialImage(res.data.file.url)
                onSuccess('ok')
              }
            }}
            onRemove={async file => {
              if (file.url) {
                await deleteFileByUrl(file.url)
              }
              setSocialImage(undefined)
              return true
            }}
            maxCount={1}
            showUploadList={{ showRemoveIcon: true }}
          >
            {!socialImage && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Tải lên</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name='featuredImage'
          label='Hình ảnh đại diện'
        >
          <Upload
            listType='picture-card'
            fileList={
              featuredImage
                ? [
                    {
                      uid: '-1',
                      name: 'featured.jpg',
                      status: 'done',
                      url: featuredImage,
                      thumbUrl: featuredImage
                    }
                  ]
                : []
            }
            customRequest={async ({ file, onSuccess }) => {
              const res = await uploadSingleFile(file as File)
              if (res.status === 'success' && onSuccess) {
                setFeaturedImage(res.data.file.url)
                onSuccess('ok')
              }
            }}
            onRemove={async file => {
              if (file.url) {
                await deleteFileByUrl(file.url)
              }
              setFeaturedImage(undefined)
              return true
            }}
            maxCount={1}
            showUploadList={{ showRemoveIcon: true }}
          >
            {!featuredImage && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Tải lên</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item className='mb-0'>
          <div className='flex gap-2 justify-end'>
            <Button onClick={handleCancel}>Hủy</Button>
            <Button type='primary' htmlType='submit' loading={loading}>
              Cập nhật bài viết
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  )
}

export default ArticleEdit
